/**
 * MarkdownRenderer
 *
 * Renders markdown content with proper styling for generated documents.
 * Uses react-markdown for parsing and rendering.
 */

import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import type { ReactNode } from 'react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

// Helper type for component props
interface ComponentProps {
  children?: ReactNode;
  className?: string;
  href?: string;
}

const components: Components = {
  h1: ({ children }: ComponentProps) => (
    <h1 className="text-2xl font-bold text-ink mb-4 mt-6 first:mt-0">{children}</h1>
  ),
  h2: ({ children }: ComponentProps) => (
    <h2 className="text-xl font-semibold text-ink mb-3 mt-5">{children}</h2>
  ),
  h3: ({ children }: ComponentProps) => (
    <h3 className="text-lg font-semibold text-ink mb-2 mt-4">{children}</h3>
  ),
  h4: ({ children }: ComponentProps) => (
    <h4 className="text-base font-semibold text-ink mb-2 mt-3">{children}</h4>
  ),
  p: ({ children }: ComponentProps) => (
    <p className="text-ink mb-3 leading-relaxed">{children}</p>
  ),
  ul: ({ children }: ComponentProps) => (
    <ul className="list-disc list-inside mb-3 space-y-1 ml-2">{children}</ul>
  ),
  ol: ({ children }: ComponentProps) => (
    <ol className="list-decimal list-inside mb-3 space-y-1 ml-2">{children}</ol>
  ),
  li: ({ children }: ComponentProps) => (
    <li className="text-ink">{children}</li>
  ),
  strong: ({ children }: ComponentProps) => (
    <strong className="font-semibold text-ink">{children}</strong>
  ),
  em: ({ children }: ComponentProps) => (
    <em className="italic">{children}</em>
  ),
  blockquote: ({ children }: ComponentProps) => (
    <blockquote className="border-l-4 border-evergreen pl-4 py-1 my-3 bg-sage/20 rounded-r">
      {children}
    </blockquote>
  ),
  code: ({ children, className }: ComponentProps) => {
    const isInline = !className;
    if (isInline) {
      return (
        <code className="bg-soft-neutral px-1.5 py-0.5 rounded text-sm font-mono">
          {children}
        </code>
      );
    }
    return (
      <code className="block bg-soft-neutral p-3 rounded text-sm font-mono overflow-x-auto mb-3">
        {children}
      </code>
    );
  },
  pre: ({ children }: ComponentProps) => (
    <pre className="bg-soft-neutral p-3 rounded overflow-x-auto mb-3">{children}</pre>
  ),
  hr: () => <hr className="border-soft-neutral my-6" />,
  a: ({ href, children }: ComponentProps) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-evergreen hover:text-teal-700 underline"
    >
      {children}
    </a>
  ),
  table: ({ children }: ComponentProps) => (
    <div className="overflow-x-auto mb-3">
      <table className="min-w-full border-collapse border border-soft-neutral">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: ComponentProps) => (
    <thead className="bg-soft-neutral">{children}</thead>
  ),
  th: ({ children }: ComponentProps) => (
    <th className="border border-soft-neutral px-3 py-2 text-left font-semibold text-ink">
      {children}
    </th>
  ),
  td: ({ children }: ComponentProps) => (
    <td className="border border-soft-neutral px-3 py-2 text-ink">{children}</td>
  ),
};

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps): JSX.Element {
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown components={components}>{content}</ReactMarkdown>
    </div>
  );
}

export default MarkdownRenderer;
